import { getGeneric, postGeneric } from '../../common/constants'

/**
 * External Shadow Run — PACE scoring for a merchant who is NOT on proship yet.
 *
 * A prospect has no merchant record, no rate card, no courier config and no
 * history, so PACE synthesises all of that from its own lake (recovered rate
 * card, empirical serviceability, observed lane zones, neutral merchant
 * defaults) and then runs the real allocator. See docs/tasks/task-08 in the PACE
 * repo.
 *
 * Calls go through proship (`/api/allocation/simulate/**`), which proxies to
 * PACE's `/pace/v1/simulate` — the same pattern as `allocation/rescore`. PACE
 * is on a private subnet, so the browser never talks to it directly.
 */
const BASE = 'allocation/simulate'

// Column contract, fetched so the sample sheet can't drift from what the parser
// accepts. There is a local fallback below for when the proxy isn't deployed yet.
export const fetchSimulateColumns = () => getGeneric(`${BASE}/columns`)

// payload = { csv_text, product_vertical?, cod_share?, presets? }
export const runExternalShadow = (payload) => postGeneric(BASE, payload)

// ── the CSV contract (fallback copy of PACE's pace/simulate/csv_spec.py) ────
// Required first, then the columns that improve fidelity, then the three that
// make the comparison possible at all.
export const FALLBACK_COLUMNS = [
  { name: 'pickup_pincode', help: '6-digit origin pincode', group: 'required' },
  { name: 'drop_pincode', help: '6-digit destination pincode', group: 'required' },
  { name: 'weight_g', help: 'shipment weight in grams', group: 'required' },
  { name: 'payment_mode', help: 'COD or PREPAID', group: 'required' },
  { name: 'length_cm', help: 'package length (cm)', group: 'recommended' },
  { name: 'breadth_cm', help: 'package breadth (cm)', group: 'recommended' },
  { name: 'height_cm', help: 'package height (cm)', group: 'recommended' },
  { name: 'final_weight_g', help: 'billed/actual weight in grams if known — overrides max(weight, L×B×H/5000) for pricing', group: 'recommended' },
  { name: 'invoice_value', help: 'order value (INR)', group: 'recommended' },
  { name: 'cod_amount', help: 'amount to collect (INR) — required when COD', group: 'recommended' },
  { name: 'order_date', help: 'YYYY-MM-DD', group: 'recommended' },
  { name: 'channel_name', help: 'e.g. SHOPIFY, Vinculum, Clickpost', group: 'recommended' },
  { name: 'sku_count', help: 'distinct SKUs in the order', group: 'recommended' },
  { name: 'item_count', help: 'total units in the order', group: 'recommended' },
  { name: 'service_type', help: 'required SLA tier (SDD/NDD/Surface) — blank if none', group: 'recommended' },
  { name: 'current_courier', help: 'courier you use today for this order', group: 'comparison' },
  { name: 'current_price_paid', help: 'freight you paid (INR)', group: 'comparison' },
  { name: 'current_outcome', help: 'DELIVERED or RTO', group: 'comparison' },
]

export const REQUIRED_COLUMNS = ['pickup_pincode', 'drop_pincode', 'weight_g', 'payment_mode']

// Two filled rows so the format is obvious without reading the help text —
// blank templates come back with dates as "15/08/26" and weights in kg.
const SAMPLE_ROWS = [
  {
    pickup_pincode: '110001', drop_pincode: '400001', weight_g: '500', payment_mode: 'PREPAID',
    length_cm: '20', breadth_cm: '15', height_cm: '10', final_weight_g: '', invoice_value: '1499',
    cod_amount: '', order_date: '2026-08-15', channel_name: 'SHOPIFY', sku_count: '1',
    item_count: '1', service_type: '', current_courier: 'Delhivery',
    current_price_paid: '62', current_outcome: 'DELIVERED',
  },
  {
    pickup_pincode: '110001', drop_pincode: '560034', weight_g: '1200', payment_mode: 'COD',
    length_cm: '25', breadth_cm: '20', height_cm: '12', final_weight_g: '', invoice_value: '2450',
    cod_amount: '2450', order_date: '2026-08-16', channel_name: 'SHOPIFY', sku_count: '2',
    item_count: '3', service_type: '', current_courier: 'Bluedart',
    current_price_paid: '118', current_outcome: 'RTO',
  },
]

const csvCell = (v) => {
  const s = v == null ? '' : String(v)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/** Sample sheet: header + two worked rows + one blank row to type into. */
export function buildSampleCsv(columns = FALLBACK_COLUMNS) {
  const header = columns.map((c) => c.name)
  const lines = [header.join(',')]
  SAMPLE_ROWS.forEach((r) => lines.push(header.map((h) => csvCell(r[h])).join(',')))
  lines.push(header.map(() => '').join(','))
  return `${lines.join('\n')}\n`
}

export function downloadTextFile(filename, text, mime = 'text/csv;charset=utf-8') {
  const url = URL.createObjectURL(new Blob([text], { type: mime }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Read an uploaded file as CSV text. Merchants export .xlsx far more often than
 * .csv, so a spreadsheet is converted here rather than bounced back at them —
 * PACE only ever receives CSV text.
 */
export async function fileToCsvText(file) {
  const name = (file.name || '').toLowerCase()
  if (name.endsWith('.csv') || name.endsWith('.txt')) return file.text()
  const XLSX = await import('xlsx')
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' })
  const sheet = wb.Sheets[wb.SheetNames[0]]
  if (!sheet) throw new Error('the spreadsheet has no sheets')
  return XLSX.utils.sheet_to_csv(sheet)
}

/** Header check done before upload, so an obviously wrong file fails instantly. */
export function missingRequiredColumns(csvText) {
  const first = (csvText || '').split(/\r?\n/)[0] || ''
  const header = first.split(',').map((h) => h.trim().toLowerCase().replace(/^"|"$/g, ''))
  return REQUIRED_COLUMNS.filter((c) => !header.includes(c))
}

/** Flatten a result into per-order rows for export. */
export function resultRowsForExport(result) {
  return (result?.rows || []).map((r) => {
    const pick = r.pick || {}
    const si = pick.simulated_inputs || {}
    const cmp = r.comparison || {}
    return {
      line: r.line,
      pickup_pincode: r.input?.pickup_pincode,
      drop_pincode: r.input?.drop_pincode,
      weight_kg: r.input?.dead_weight_kg,
      // weight PACE priced on (task #11): max(dead, L×B×H/5000) or the supplied billed weight
      dead_weight_g: r.weight?.dead_weight_g,
      volumetric_weight_g: r.weight?.volumetric_weight_g,
      chargeable_weight_g: r.weight?.chargeable_weight_g,
      priced_weight_g: r.weight?.final_weight_g,
      weight_source: r.weight?.weight_source,
      payment_mode: r.input?.payment_mode,
      pace_pick: pick.prozo_id,
      pace_quote: pick.quote,
      zone: si.zone,
      zone_source: si.zone_source,
      price_source: si.price_source,
      quality_source: pick.unified?.quality_source,
      eta_hours: pick.est_transit_hours,
      candidates: r.resolution?.candidates,
      priced: r.resolution?.priced,
      current_courier: cmp.current_courier,
      current_price_paid: cmp.current_price_paid,
      price_delta: cmp.price_delta,
      same_courier_as_today: cmp.same_courier,
      // PACE+ #4: one pick / quote / ETA column trio per compared preset
      ...Object.fromEntries(Object.entries(r.presets || {}).flatMap(([k, p]) => [
        [`${k}_pick`, p?.prozo_id],
        [`${k}_quote`, p?.quote],
        [`${k}_eta_hours`, p?.est_transit_hours],
      ])),
    }
  })
}
